/**
 * Client-side analytics helper.
 *
 * All calls are safe when analytics is not loaded (user declined or blocked
 * localStorage). We check `window.ssAnalyticsLoaded` before invoking gtag,
 * so decliners never touch Google's servers.
 *
 * ── Events we track (standard names — use these consistently) ──────────
 *
 *   page_view              auto, on React Router navigation (AnalyticsTracker)
 *   call_click             any <a href="tel:..."> click (delegated in index.html)
 *   email_click            any <a href="mailto:..."> click (delegated)
 *   book_click             tapped "Book Online" / "Place Order" / "Book a Delivery"
 *   track_click            tapped "Track" button
 *   quote_generated        calculator computed a live price
 *   pricing_calculator_click     tapped "Open the calculator" from /pricing
 *   business_account_click       tapped "Open Business Account"
 *   whatsapp_click               tapped WhatsApp button (future)
 *   form_submit                  submitted a form (booking / signup / contact)
 *
 * Each event carries `page_path` automatically. Add `source` param via
 * `data-track-source="..."` on the element (e.g. hero, header, footer,
 * pricing_hero, mobile_menu) so GA4 breakdowns can compare CTAs.
 *
 * ── Mark as "Key Events" in GA4 ─────────────────────────────────────────
 *
 * By default GA4 records these as regular events. To count them as
 * CONVERSIONS in reports, mark each as a "Key Event" in GA4 Admin:
 *   Admin → Events → toggle "Mark as key event" ON
 *
 * Recommended key events for SpeedyShip: book_click, call_click,
 * quote_generated, form_submit.
 */

export const EVENTS = {
  PAGE_VIEW:                 'page_view',
  CALL_CLICK:                'call_click',
  EMAIL_CLICK:               'email_click',
  BOOK_CLICK:                'book_click',
  TRACK_CLICK:               'track_click',
  QUOTE_GENERATED:           'quote_generated',
  PRICING_CALCULATOR_CLICK:  'pricing_calculator_click',
  BUSINESS_ACCOUNT_CLICK:    'business_account_click',
  WHATSAPP_CLICK:            'whatsapp_click',
  FORM_SUBMIT:               'form_submit',
  // Fired on any successful lead-form submit (order, contact, landing-page
  // quote). Mapped to BOTH the GA4 import conversion AND the native Google
  // Ads website conversion in Google Ads — one is Primary (native, for
  // bidding), the other Secondary (import, for reporting continuity).
  GENERATE_LEAD:             'generate_lead',
} as const;

declare global {
  interface Window {
    ssAnalyticsLoaded?: boolean;
    ssLoadAnalytics?: () => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    dataLayer?: any[];
  }
}

export const CONSENT_KEY = 'speedyship_consent';
export type ConsentValue = 'accepted' | 'declined';

export function getConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === 'accepted' || v === 'declined' ? v : null;
  } catch {
    return null;
  }
}

export function setConsent(value: ConsentValue): void {
  try { localStorage.setItem(CONSENT_KEY, value); } catch { /* ignore */ }
  if (value === 'accepted') {
    window.ssLoadAnalytics?.();
  }
}

/** Fire a GA4 event only if the user opted in. */
export function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean> = {},
): void {
  if (!window.ssAnalyticsLoaded || !window.gtag) return;
  window.gtag('event', eventName, params);
}

/**
 * Emit a GA4 page_view. Called on every React Router navigation from
 * AppNew.tsx so single-page-app route changes are counted correctly.
 */
export function trackPageview(path: string, title?: string): void {
  if (!window.ssAnalyticsLoaded || !window.gtag) return;
  window.gtag('event', 'page_view', {
    page_path:  path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}
